/**
 * Passive Event Listeners Setup
 *
 * This utility automatically makes certain event listeners passive to improve
 * scroll performance and eliminate browser warnings about non-passive event
 * listeners on scroll-blocking events.
 *
 * This is a workaround for third-party libraries (like HeadlessUI) that
 * don't set their event listeners as passive.
 */

type EventListenerOptions = boolean | AddEventListenerOptions;

const passiveEvents = ['wheel', 'touchstart', 'touchmove'];

function makePassive() {
  // Store the original addEventListener method
  const originalAddEventListener = EventTarget.prototype.addEventListener;

  // Override addEventListener to automatically add passive option for specific events
  EventTarget.prototype.addEventListener = function (
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: EventListenerOptions,
  ) {
    // Check if this event should be passive
    if (passiveEvents.includes(type)) {
      // If options is a boolean or undefined, convert to object
      if (typeof options === 'boolean' || options === undefined) {
        options = {
          capture: typeof options === 'boolean' ? options : false,
          passive: true,
        };
      }
      else if (typeof options === 'object') {
        // If options is already an object, add passive: true unless explicitly set to false
        options = {
          ...options,
          passive: options.passive !== false,
        };
      }
    }

    // Call the original addEventListener with the potentially modified options
    return originalAddEventListener.call(this, type, listener, options);
  };
}

export { makePassive };
